import * as React from 'react';
import { Container, Form, Button } from 'semantic-ui-react';
import ReCAPTCHA from 'react-google-recaptcha';
import * as Sentry from '@sentry/browser';
import InputFile from '../InputFile';
import {
  submitFormData,
  getBase64,
  validateInputType,
  validateInputSize,
} from '../../helpers/form-api';
import { recaptchaSize } from '../../helpers/common';
import ThankYou, { ThankYouType } from './partials/ThankYou';

const recaptchaKey =
  process.env.GATSBY_GOOGLE_RECAPTCHA_KEY ||
  '6LcAdI8UAAAAAH6_Ze1qFIg9ny_kw2L7XeG8qJjL';

class ContactForm extends React.Component {
  initialFormData = {
    subject: '',
    customer_type: '',
    enquiry: '',
    postcode: '',
    meter_number: '',
    city_suburb: '',
    street_address: '',
    phone: '',
    first_name: '',
    last_name: '',
    state: '',
    email: '',
  };

  state = {
    error: null,
    success: false,
    emailData: undefined,
    submitting: false,
    formData: { ...this.initialFormData },
    errors: {},
  };

  fileInput = React.createRef();
  fileInputWrapper = React.createRef();
  recaptchaRef = React.createRef();

  handleCaptcha = (value) => {
    this.setState({ captcha: value });
  };

  handleInputChange = (event) => {
    const target = event.currentTarget;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    this.setState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  };

  handleSelectChange = (event, { name, value }) => {
    this.setState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  };

  validateInputs = () => {
    const {
      formData: { customer_type: customerType },
    } = this.state;
    let valid = true;

    if (!customerType) {
      this.setState((prevState) => ({
        ...prevState,
        errors: {
          ...prevState.errors,
          customerType: 'Required',
        },
      }));
      valid = false;
    } else {
      this.setState((prevState) => ({
        ...prevState,
        errors: {
          ...prevState.errors,
          customerType: undefined,
        },
      }));
    }

    let file;
    if (this.fileInput.current) {
      file = this.fileInput.current.files[0];
      if (
        file &&
        (!validateInputType(file.type) || !validateInputSize(file.size, 5))
      ) {
        this.setState((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            attachment: 'FileType',
          },
        }));
        valid = false;
      } else {
        this.setState((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            attachment: undefined,
          },
        }));
      }
    }

    return valid;
  };

  handleSubmit = async () => {
    try {
      this.setState({ success: false, error: null, submitting: true });

      const valid = this.validateInputs();
      if (!valid) {
        this.setState({
          error: 'Missing field.',
          success: false,
          submitting: false,
        });
        return;
      }

      if (this.state.captcha) {
        let file;
        if (this.fileInput.current) {
          file = this.fileInput.current.files[0];
        }
        let attachment;
        if (file) {
          attachment = {
            content: await getBase64(file),
            name: file.name,
          };
          const fileSize = file.size / 1024 / 1024; // in MB
          if (fileSize > 5) {
            this.setState({
              success: false,
              error: 'File size exceeds 5 MB.',
              submitting: false,
            });
            return;
          }
        }

        const formData = {
          ...this.state.formData,
          attachment,
          captchaResponse: this.state.captcha,
        };

        const response = await submitFormData('general_enquiry', formData);

        if (response.status !== 200) {
          this.setState({
            error: response.message,
            success: false,
            submitting: false,
            captcha: null,
          });
        } else {
          this.setState({
            success: true,
            emailData: response.emailData,
            error: null,
            submitting: false,
            formData: { ...this.initialFormData },
            captcha: null,
          });

          if (this.fileInputWrapper.current) {
            this.fileInputWrapper.current.clear();
          }
        }

        if (this.recaptchaRef.current) {
          this.recaptchaRef.current.reset();
        }
      } else {
        this.setState({
          success: false,
          error: 'Please tick ReCAPTCHA',
          submitting: false,
        });
      }
    } catch (err) {
      console.error(err);
      Sentry.captureException(err);
      this.setState({ error: 'Error', success: false, submitting: false });
    }
  };

  submitText = () => {
    if (this.state.submitting) {
      return 'Submitting...';
    } else if (this.state.success) {
      return 'Submitted';
    }
    return 'Submit';
  };

  render() {
    const { formData } = this.state;
    const customerTypeOptions = [
      {
        text: 'I am a CitiPower customer',
        key: 'I am a CitiPower customer',
        value: 'I am a CitiPower customer',
      },
      {
        text: 'I am a Powercor customer',
        key: 'I am a Powercor customer',
        value: 'I am a Powercor customer',
      },
      {
        text: 'I am not a customer',
        key: 'I am not a customer',
        value: 'I am not a customer',
      },
      { text: 'I am unsure', key: 'I am unsure', value: 'I am unsure' },
    ];
    const {
      errors: { customerType: customerTypeError, attachment: attachmentError },
    } = this.state;

    if (this.state.success) {
      return (
        <ThankYou
          emailData={this.state.emailData}
          type={ThankYouType.GENERAL_ENQUIRY}
        />
      );
    }

    return (
      <section>
        <Container>
          <div className="grid-wrapper">
            <div className="form-container">
              <Form className="form-container" onSubmit={this.handleSubmit}>
                <Form.Input
                  label="Subject"
                  type="text"
                  name="subject"
                  value={formData.subject}
                  required
                  onChange={this.handleInputChange}
                  id="form-subject"
                />

                <Form.TextArea
                  label="What is your enquiry about?"
                  name="enquiry"
                  value={formData.enquiry}
                  required
                  onChange={this.handleInputChange}
                  id="form-enquiry"
                />

                <InputFile
                  label="Attachment"
                  name="attachment"
                  text="No file chosen"
                  restrictions="Max. filesize 3MB. Please upload only pdf, doc, docx, xls, xslx, pages, numbers, jpg, tiff, bmp, png, txt, csv, rtf, odt or ods files. Note that if form submission fails, please re-upload attachment."
                  maxFileSize={3}
                  refProp={this.fileInput}
                  ref={this.fileInputWrapper}
                  error={attachmentError != null}
                />

                <h4>Your details</h4>
                <Form.Select
                  label="Customer type"
                  required
                  options={customerTypeOptions}
                  placeholder="Please choose"
                  name="customer_type"
                  onChange={this.handleSelectChange}
                  value={formData.customer_type}
                  id="form-customer-type"
                  error={customerTypeError != null}
                />

                <Form.Input
                  label="First name"
                  required
                  name="first_name"
                  onChange={this.handleInputChange}
                  value={formData.first_name}
                  id="form-first-name"
                />

                <Form.Input
                  label="Last name"
                  required
                  name="last_name"
                  onChange={this.handleInputChange}
                  value={formData.last_name}
                  id="form-last-name"
                />

                <Form.Input
                  onChange={this.handleInputChange}
                  label="Email address"
                  required
                  type="email"
                  name="email"
                  value={formData.email}
                  id="form-email"
                />

                <Form.Input
                  label="Phone number"
                  required
                  type="phone"
                  name="phone"
                  onChange={this.handleInputChange}
                  value={formData.phone}
                  id="form-phone"
                />

                <Form.Input
                  label="Street address"
                  name="street_address"
                  onChange={this.handleInputChange}
                  value={formData.street_address}
                  id="form-street-address"
                />

                <Form.Input
                  label="City/Suburb"
                  name="city_suburb"
                  onChange={this.handleInputChange}
                  value={formData.city_suburb}
                  id="form-city-suburb"
                />

                <Form.Input
                  label="State"
                  name="state"
                  onChange={this.handleInputChange}
                  value={formData.state}
                  id="form-state"
                />

                <Form.Input
                  label="Postcode"
                  name="postcode"
                  onChange={this.handleInputChange}
                  value={formData.postcode}
                  id="form-postcode"
                />

                <Form.Field>
                  <Form.Input
                    label="National Meter Identifier (NMI)"
                    name="meter_number"
                    id="form-meter-number"
                    onChange={this.handleInputChange}
                    value={formData.meter_number}
                  />
                </Form.Field>
                <div className="form-field__description">
                  Your NMI number appears on the bill you receive from your
                  retailer
                </div>

                <ReCAPTCHA
                  ref={this.recaptchaRef}
                  sitekey={recaptchaKey}
                  onChange={this.handleCaptcha}
                  size={recaptchaSize()}
                />

                <Button
                  className="form--submit-btn"
                  disabled={this.state.submitting}
                  loading={this.state.submitting}
                >
                  {this.submitText()}
                </Button>

                {this.state.error ? (
                  <div className="error-message">{this.state.error}</div>
                ) : null}
              </Form>
            </div>
          </div>
        </Container>
      </section>
    );
  }
}

export default ContactForm;
